import React from "react";
import { Form, Field, FormSpy } from "react-final-form";
import { RowWrap, Col, Button } from "@govlaunch/core";
import LogoField from "~/components/form/LogoField";
import ResourcesField from "~/components/form/ResourcesField";
import * as palette from "@govlaunch/palette";
import ProductsField from "~/components/group/form/fields/ProductsField";
import * as Yup from "yup";
import { Margin } from "~/components/spacings";
import validate from "~/lib/utils/validate";
import TextField from "~/components/form/TextField";
import TagsSelectContainer from "~/components/tag/TagsSelect";
import { ITagType } from "~/types/types";
import SocialMediaFields from "~/components/form/SocialMediaFields";
import Section from "~/components/form/sections/Section";
import SectionHeader from "~/components/form/sections/SectionHeader";
import SectionBody from "~/components/form/sections/SectionBody";
import TextareaField from "~/components/form/TextareaField";
import NewsworthyField from "~/components/form/NewsworthyField";
import { useIntl, FormattedMessage } from "react-intl";
import useIntlValidationMessages from "~/lib/hooks/useIntlValidationMessages";

interface IGroupFormProps {
  onSubmit: (values: any) => void;
  initialValues: { [key: string]: any };
}

export default function GroupForm({ onSubmit, initialValues }: IGroupFormProps) {
  const intl = useIntl();
  const intlValidationMessages = useIntlValidationMessages();

  const validationSchema = Yup.object().shape({
    name: Yup.string().required(intlValidationMessages.cantBeBlank),
    location: Yup.string().required(intlValidationMessages.cantBeBlank),
    about: Yup.string()
      .min(20, intlValidationMessages.minCharacters(20))
      .max(800, intlValidationMessages.maxCharacters(800))
      .required(intlValidationMessages.cantBeBlank),
    website: Yup.string().url(intlValidationMessages.invalidUrl).required(intlValidationMessages.cantBeBlank),
    socialMedia: Yup.object().shape({
      twitter: Yup.string().url(intlValidationMessages.invalidUrl).nullable(),
      youtube: Yup.string().url(intlValidationMessages.invalidUrl).nullable(),
      facebook: Yup.string().url(intlValidationMessages.invalidUrl).nullable(),
      linkedin: Yup.string().url(intlValidationMessages.invalidUrl).nullable(),
      instagram: Yup.string().url(intlValidationMessages.invalidUrl).nullable(),
    }),
  });

  return (
    <Form onSubmit={onSubmit} initialValues={initialValues} validate={validate(validationSchema)}>
      {({ handleSubmit, submitting, submitError }) => (
        <form onSubmit={handleSubmit} autoComplete="off">
          <RowWrap>
            <Col width={[1, 8 / 12]}>
              <Section>
                <SectionHeader>
                  <FormattedMessage defaultMessage="The Basics" id="5L4CQD" />
                </SectionHeader>
                <SectionBody>
                  <TextField
                    fieldName="name"
                    placeholder={intl.formatMessage({
                      defaultMessage: `What's the group name?`,
                      id: "x5XMh4",
                    })}
                    label={intl.formatMessage({ defaultMessage: "Name", id: "HAlOn1" })}
                  />
                  <TextField
                    fieldName="website"
                    placeholder={intl.formatMessage({
                      defaultMessage: "Group website URL (http://)",
                      id: "Ghv4R/",
                    })}
                    label={intl.formatMessage({ defaultMessage: "Website", id: "JkLHGw" })}
                  />
                  <TextField
                    fieldName="location"
                    placeholder={intl.formatMessage({
                      defaultMessage: `What's the group location?`,
                      id: "obj3GZ",
                    })}
                    label={intl.formatMessage({ defaultMessage: "Location", id: "rvirM2" })}
                  />
                </SectionBody>
              </Section>

              <Margin mt={24}>
                <Section>
                  <SectionHeader
                    css={{
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <FormattedMessage defaultMessage="About the Group" id="5zui5m" />
                  </SectionHeader>
                  <SectionBody>
                    <TextareaField
                      fieldName="about"
                      placeholder={intl.formatMessage({
                        defaultMessage: "Tell a bit more about the group",
                        id: "oLtRDR",
                      })}
                    />
                  </SectionBody>
                </Section>
              </Margin>

              <Margin mt={24}>
                <Section>
                  <SectionHeader>
                    <FormattedMessage defaultMessage="Newsworthy" id="JhItYg" />
                  </SectionHeader>

                  <SectionBody
                    css={{
                      padding: 0,
                    }}
                  >
                    <NewsworthyField fieldName="newsworthy" />
                  </SectionBody>
                </Section>
              </Margin>

              <Margin mt={24}>
                <Section css={{ overflow: "visible" }}>
                  <SectionHeader>
                    <FormattedMessage defaultMessage="Products" id="7NFfmz" />
                  </SectionHeader>
                  <SectionBody
                    css={{
                      padding: 0,
                    }}
                  >
                    <ProductsField />
                  </SectionBody>
                </Section>
              </Margin>
            </Col>
            <Col width={[1, 4 / 12]}>
              <Section>
                <SectionHeader>
                  <FormattedMessage defaultMessage="Logo" id="k81S1y" />
                </SectionHeader>
                <SectionBody>
                  <FormSpy subscription={{ values: true }}>
                    {({ values }) => <LogoField fieldName="logo" entityName={values.name} />}
                  </FormSpy>
                </SectionBody>
              </Section>

              <Margin mt={24}>
                <Section>
                  <SectionHeader>
                    <FormattedMessage defaultMessage="Social Media" id="5bGX59" />
                  </SectionHeader>
                  <SectionBody>
                    <SocialMediaFields />
                  </SectionBody>
                </Section>
              </Margin>

              <Margin mt={24}>
                <Section>
                  <SectionHeader>
                    <FormattedMessage defaultMessage="🏅 Awards" id="RT1ZpG" />
                  </SectionHeader>

                  <SectionBody>
                    <Field name="awards">
                      {({ input }) => <TagsSelectContainer {...input} type={ITagType.Award} />}
                    </Field>
                  </SectionBody>
                </Section>
              </Margin>

              <Margin mt={24}>
                <Section>
                  <SectionHeader>
                    <FormattedMessage defaultMessage="Resources" id="c/KktL" />
                  </SectionHeader>

                  <SectionBody>
                    <ResourcesField fieldName="resources" />
                  </SectionBody>
                </Section>
              </Margin>

              <Margin mt={24}>
                <Button
                  type="submit"
                  color={palette.primary}
                  size="large"
                  disabled={submitting}
                  style={{
                    width: "100%",
                  }}
                >
                  {submitting ? (
                    <FormattedMessage defaultMessage="Submitting" id="zA7EJD" />
                  ) : (
                    <FormattedMessage defaultMessage="Submit" id="wSZR47" />
                  )}
                </Button>

                <p
                  css={{
                    marginTop: 4,
                    color: palette.red,
                  }}
                >
                  {submitError}
                </p>
              </Margin>
            </Col>
          </RowWrap>
        </form>
      )}
    </Form>
  );
}
